<!--
  Modal — primitiva de diálogo de Otera, enriquecida para uso en producción.

  Añade sobre el mockup original: <teleport>, focus-trap + restauración de
  foco, cierre con ESC, bloqueo de scroll del body y aria-labelledby.

  Props:
    closeOnBackdrop — si false, el click en el backdrop es NO-OP (UX-DR16,
                      acciones destructivas). Por defecto true.
    closeOnEsc      — si false, ESC no cierra. Por defecto true.

  El cierre siempre se comunica vía `@close`; el padre controla `open`.
-->

<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';

import Icon from './Icon.vue';

defineOptions({ name: 'DsModal' });

const props = defineProps({
  open: { type: Boolean, default: false },
  title: { type: String, default: null },
  subtitle: { type: String, default: null },
  size: {
    type: String,
    default: 'md',
    validator: (v) => ['sm', 'md', 'lg'].includes(v)
  },
  closeOnBackdrop: { type: Boolean, default: true },
  closeOnEsc: { type: Boolean, default: true }
});

const emit = defineEmits(['close']);

let modalSeq = 0;
const titleId = `ds-modal-title-${(modalSeq += 1)}`;

const panelRef = ref(null);
let previouslyFocused = null;

function getFocusables() {
  if (!panelRef.value) return [];
  return Array.from(
    panelRef.value.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )
  );
}

async function focusFirst() {
  await nextTick();
  const focusables = getFocusables();
  if (focusables.length) focusables[0].focus();
  else panelRef.value?.focus();
}

function onKeydown(e) {
  if (e.key === 'Escape' && props.closeOnEsc) {
    e.preventDefault();
    emit('close');
    return;
  }
  if (e.key !== 'Tab') return;
  const focusables = getFocusables();
  if (focusables.length === 0) {
    e.preventDefault();
    return;
  }
  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  const active = document.activeElement;
  if (e.shiftKey) {
    if (active === first || !panelRef.value?.contains(active)) {
      e.preventDefault();
      last.focus();
    }
  } else if (active === last) {
    e.preventDefault();
    first.focus();
  }
}

function onBackdrop() {
  if (props.closeOnBackdrop) emit('close');
}

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      previouslyFocused = document.activeElement;
      document.body.style.overflow = 'hidden';
      focusFirst();
    } else {
      document.body.style.overflow = '';
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
      previouslyFocused = null;
    }
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  document.body.style.overflow = '';
});

const panelClass = computed(() => ['ds-modal__panel', `ds-modal__panel--${props.size}`]);
</script>

<template>
  <teleport to="body">
    <div
      v-if="open"
      class="ds-modal"
      @keydown="onKeydown"
    >
      <!-- eslint-disable vuejs-accessibility/click-events-have-key-events —
           el backdrop es un atajo de ratón; el teclado cierra con ESC (onKeydown). -->
      <div
        class="ds-modal__backdrop"
        @click="onBackdrop"
      />
      <!-- eslint-enable vuejs-accessibility/click-events-have-key-events -->
      <div
        ref="panelRef"
        :class="panelClass"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="title ? titleId : undefined"
        tabindex="-1"
      >
        <div
          v-if="title || $slots.header"
          class="ds-modal__header"
        >
          <slot name="header">
            <div>
              <div
                :id="titleId"
                class="ds-modal__title"
              >
                {{ title }}
              </div>
              <div
                v-if="subtitle"
                class="ds-modal__subtitle"
              >
                {{ subtitle }}
              </div>
            </div>
          </slot>
          <button
            type="button"
            class="ds-modal__close"
            aria-label="Close"
            @click="emit('close')"
          >
            <Icon
              name="x"
              :size="14"
              :stroke="2"
            />
          </button>
        </div>
        <div class="ds-modal__body">
          <slot />
        </div>
        <div
          v-if="$slots.footer"
          class="ds-modal__footer"
        >
          <slot name="footer" />
        </div>
      </div>
    </div>
  </teleport>
</template>
